import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Layout Components
import AppLayout from './components/layout/AppLayout';
import AuthLayout from './components/layout/AuthLayout';

// Page Components
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import CreateProject from './pages/CreateProject';
import Analytics from './pages/Analytics';
import Team from './pages/Team';
import Settings from './pages/Settings';
import Chat from './pages/Chat';
import Documents from './pages/Documents';
import Integrations from './pages/Integrations';
import ClientPortal from './pages/ClientPortal';
import Approvals from './pages/Approvals';
import AIInsights from './pages/AIInsights';
import EnterpriseOnboarding from './pages/EnterpriseOnboarding';
import LandingPage from './pages/LandingPage';
import GettingStarted from './pages/GettingStarted';
import Help from './pages/Help';
import Login from './pages/auth/Login';
import NotFound from './pages/NotFound';

// Providers
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';

// SSO Handler
import SSOTokenHandler from './components/SSOTokenHandler';

// Lightweight auth guard — redirects to login if not authenticated
function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  return children;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <Router>
            <SSOTokenHandler>
            <div className="App">
              <Routes>
                {/* Public Landing Page - No auth required (exact match) */}
                <Route path="/" element={<LandingPage />} />

                {/* Auth Routes */}
                <Route path="/auth/*" element={<AuthLayout />}>
                  <Route path="login" element={<Login />} />
                  <Route path="*" element={<Navigate to="/auth/login" replace />} />
                </Route>

                {/* Onboarding Route - Requires authentication */}
                <Route path="/onboarding" element={<RequireAuth><EnterpriseOnboarding /></RequireAuth>} />

                {/* Main App Routes - Protected */}
                <Route path="/dashboard" element={<AppLayout />}>
                  <Route index element={<Dashboard />} />
                </Route>
                <Route path="/projects" element={<AppLayout />}>
                  <Route index element={<Projects />} />
                  <Route path="new" element={<CreateProject />} />
                  <Route path=":projectId" element={<ProjectDetail />} />
                </Route>
                <Route path="/analytics" element={<AppLayout />}>
                  <Route index element={<Analytics />} />
                </Route>
                <Route path="/ai-insights" element={<AppLayout />}>
                  <Route index element={<AIInsights />} />
                </Route>
                <Route path="/team" element={<AppLayout />}>
                  <Route index element={<Team />} />
                </Route>
                <Route path="/chat" element={<AppLayout />}>
                  <Route index element={<Chat />} />
                </Route>
                <Route path="/documents" element={<AppLayout />}>
                  <Route index element={<Documents />} />
                </Route>
                <Route path="/integrations" element={<AppLayout />}>
                  <Route index element={<Integrations />} />
                </Route>
                <Route path="/approvals" element={<AppLayout />}>
                  <Route index element={<Approvals />} />
                </Route>
                <Route path="/getting-started" element={<AppLayout />}>
                  <Route index element={<GettingStarted />} />
                </Route>
                <Route path="/client-portal" element={<AppLayout />}>
                  <Route index element={<ClientPortal />} />
                </Route>
                <Route path="/help" element={<AppLayout />}>
                  <Route index element={<Help />} />
                </Route>
                <Route path="/settings" element={<AppLayout />}>
                  <Route index element={<Settings />} />
                </Route>
                <Route path="/404" element={<AppLayout />}>
                  <Route index element={<NotFound />} />
                </Route>
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>

              {/* Global Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#fff',
                    color: '#374151',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    padding: '16px',
                  },
                  success: {
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </SSOTokenHandler>
          </Router>
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
